import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Calendar,
  ChevronRight,
  ExternalLink,
  Hash,
  Loader2,
  Mail,
  Package,
  Phone,
  RefreshCcw,
  Tag,
  User,
  X,
} from "lucide-react";
import moment from "moment";
import { toast } from "react-toastify";
import OrdersRequest from "../../requests/vendor/OrdersRequest";
import { DealsRequest } from "../../requests/vendor/DealsRequest";
import OrderStatusBadge from "./OrderStatusBadge";
import { formatCurrency } from "../../utils/formatCurrency";
import ViewOffer from "../Offers/ViewOffer";

// "View Order Items" modal — fetches the full order breakdown on open
// (items, vendor summary, order summary, customer info) via
// GET /api/vendor/orders/{order_code}.
const OrderDetailsModal = ({ isOpen, orderCode, initialStatus, onClose, onUpdateStatus }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [viewOfferDeal, setViewOfferDeal] = useState(null);
  const [dealLoadingSlug, setDealLoadingSlug] = useState(null);

  const handleViewDeal = async (dealSlug) => {
    if (!dealSlug || dealLoadingSlug) return;
    setDealLoadingSlug(dealSlug);
    const result = await DealsRequest.getDealBySlugTransformed(dealSlug);
    setDealLoadingSlug(null);
    if (result.success) {
      setViewOfferDeal(result.data);
    } else {
      toast.error(result.message || "Could not load deal details");
    }
  };

  useEffect(() => {
    if (!isOpen || !orderCode) return undefined;

    let isMounted = true;
    setDetails(null);
    setErrorMessage("");

    const fetchDetails = async () => {
      setIsLoading(true);
      const result = await OrdersRequest.getOrderDetails(orderCode);
      if (!isMounted) return;

      if (result.success && result.data?.status_code === "000" && !result.data?.in_error) {
        setDetails(result.data.data);
      } else {
        setErrorMessage(result.data?.reason || result.message || "Failed to load order details");
      }
      setIsLoading(false);
    };

    fetchDetails();
    return () => {
      isMounted = false;
    };
  }, [isOpen, orderCode]);

  if (!isOpen) return null;

  const orderSummary = details?.order_summary;
  const vendorSummary = details?.vendor_summary;

  const portal = createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 backdrop-blur-sm bg-black/40"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="flex overflow-auto fixed inset-0 z-50 justify-center items-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative my-8 w-full max-w-2xl bg-white rounded-2xl shadow-2xl">
          <div className="flex sticky top-0 z-10 gap-3 items-start px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
            <div className="h-9 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-2 items-center">
                <h3 className="text-lg font-semibold text-gray-900">Order Items</h3>
                {initialStatus && <OrderStatusBadge status={initialStatus} />}
              </div>
              <div className="flex gap-1.5 items-center mt-1 text-xs text-gray-500">
                <Hash className="w-3 h-3" />
                <span className="font-mono">{orderCode}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="px-6 py-5 max-h-[calc(100vh-16rem)] overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col gap-3 justify-center items-center py-16">
                <Loader2 className="w-7 h-7 text-[#FF6C6C] animate-spin" />
                <p className="text-sm text-gray-500">Loading order details...</p>
              </div>
            ) : errorMessage ? (
              <div className="flex flex-col gap-3 justify-center items-center py-16 text-center">
                <div className="flex justify-center items-center w-12 h-12 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <p className="max-w-sm text-sm text-gray-600">{errorMessage}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {details?.user && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                      <div className="flex gap-2 items-center mb-1 text-gray-400">
                        <User className="w-3.5 h-3.5" />
                        <span className="text-[11px] font-semibold tracking-wide uppercase">Customer</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate">{details.user.full_name}</p>
                    </div>
                    <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                      <div className="flex gap-2 items-center mb-1 text-gray-400">
                        <Mail className="w-3.5 h-3.5" />
                        <span className="text-[11px] font-semibold tracking-wide uppercase">Email</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate">{details.user.email || "—"}</p>
                    </div>
                    <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                      <div className="flex gap-2 items-center mb-1 text-gray-400">
                        <Phone className="w-3.5 h-3.5" />
                        <span className="text-[11px] font-semibold tracking-wide uppercase">Phone</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {details.user.phone_number || "—"}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex gap-2 items-center mb-3">
                    <Package className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-700">
                      Purchased Items ({details?.items?.length || 0})
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {(details?.items || []).map((item, idx) => {
                      const isItemLoading = dealLoadingSlug === item.deal_slug;
                      const size =
                        item.size != null && String(item.size).trim()
                          ? String(item.size).trim()
                          : null;
                      return (
                        <button
                          key={`${item.deal_slug}-${size || "nosize"}-${idx}`}
                          type="button"
                          onClick={() => handleViewDeal(item.deal_slug)}
                          disabled={!!dealLoadingSlug}
                          className="group relative flex w-full gap-4 items-center p-3 bg-gray-50/70 hover:bg-[#fff5f5] rounded-xl border border-gray-100 hover:border-[#FF6C6C]/30 transition-all duration-200 text-left cursor-pointer"
                        >
                          <div className="relative flex-shrink-0">
                            <img
                              src={item.deal_image}
                              alt={item.deal_title}
                              className="object-cover w-16 h-16 bg-white rounded-lg border border-gray-200"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/default-image.png";
                              }}
                            />
                            {isItemLoading && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
                                <Loader2 className="w-5 h-5 text-[#FF6C6C] animate-spin" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#FF6C6C] transition-colors">
                                {item.deal_title}
                              </p>
                              <ExternalLink className="w-3 h-3 text-gray-300 group-hover:text-[#FF6C6C] flex-shrink-0 transition-colors" />
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 items-center mt-1.5 text-xs text-gray-500">
                              {size ? (
                                <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-700">
                                  Size {size}
                                </span>
                              ) : null}
                              <span>
                                Qty <span className="font-medium text-gray-700">{item.quantity}</span>
                              </span>
                              <span className="flex gap-1.5 items-center">
                                Unit <span className="font-medium text-gray-700">{formatCurrency(item.unit_price)}</span>
                                {item.original_price > item.unit_price && (
                                  <span className="text-gray-400 line-through">
                                    {formatCurrency(item.original_price)}
                                  </span>
                                )}
                              </span>
                              {item.affiliate_code && (
                                <span className="inline-flex gap-1 items-center px-2 py-0.5 font-medium text-purple-600 bg-purple-50 rounded-full">
                                  <Tag className="w-3 h-3" />
                                  {item.affiliate_code}
                                </span>
                              )}
                            </div>
                            <p className="mt-1.5 text-[10px] font-medium text-gray-300 group-hover:text-[#FF6C6C]/60 transition-colors">
                              Click to view deal details
                            </p>
                          </div>
                          <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                            <p className="text-sm font-bold text-gray-900">{formatCurrency(item.line_total)}</p>
                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF6C6C] transition-colors" />
                          </div>
                        </button>
                      );
                    })}
                    {(!details?.items || details.items.length === 0) && (
                      <p className="py-4 text-sm text-center text-gray-400">No items found for this order.</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {vendorSummary && (
                    <div className="p-4 bg-white rounded-xl border border-gray-100">
                      <p className="mb-3 text-xs font-semibold tracking-wide text-gray-400 uppercase">
                        Your Items Summary
                      </p>
                      <div className="flex justify-between mb-1.5 text-sm">
                        <span className="text-gray-500">Items</span>
                        <span className="font-medium text-gray-800">{vendorSummary.total_items}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Subtotal</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(vendorSummary.subtotal)}
                        </span>
                      </div>
                    </div>
                  )}

                  {orderSummary && (
                    <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100">
                      <p className="mb-3 text-xs font-semibold tracking-wide text-gray-400 uppercase">
                        Full Order Summary
                      </p>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Subtotal</span>
                          <span className="font-medium text-gray-800">{formatCurrency(orderSummary.subtotal)}</span>
                        </div>
                        {orderSummary.savings > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Savings</span>
                            <span className="font-medium text-green-600">
                              -{formatCurrency(orderSummary.savings)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-500">Delivery Fee</span>
                          <span className="font-medium text-gray-800">
                            {orderSummary.delivery_fee
                              ? formatCurrency(orderSummary.delivery_fee)
                              : "Free"}
                          </span>
                        </div>
                        {orderSummary.discount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Discount</span>
                            <span className="font-medium text-green-600">
                              -{formatCurrency(orderSummary.discount)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between pt-2 mt-1 border-t border-gray-200">
                          <span className="text-sm font-semibold text-gray-700">Total</span>
                          <span className="text-base font-bold text-gray-900">
                            {formatCurrency(orderSummary.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 items-center px-1 text-xs text-gray-400">
                  <span className="flex gap-1.5 items-center">
                    <Calendar className="w-3.5 h-3.5" />
                    Placed {moment(details?.created_at).format("DD MMM YYYY, hh:mm A")}
                  </span>
                  {details?.updated_at && details.updated_at !== details.created_at && (
                    <span className="flex gap-1.5 items-center">
                      <RefreshCcw className="w-3.5 h-3.5" />
                      Updated {moment(details.updated_at).format("DD MMM YYYY, hh:mm A")}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex sticky bottom-0 gap-3 justify-end px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
            >
              Close
            </button>
            {onUpdateStatus && (
              <button
                onClick={onUpdateStatus}
                className="inline-flex gap-2 items-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl shadow-sm transition-all duration-200 hover:opacity-90 hover:shadow-md"
              >
                <RefreshCcw className="w-4 h-4" />
                Update Status
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );

  return (
    <>
      {portal}
      {viewOfferDeal &&
        createPortal(
          <ViewOffer
            viewModal
            onCloseViewModal={() => setViewOfferDeal(null)}
            deal={viewOfferDeal}
            handleEditNavigation={(deal) => navigate(`/offers/edit/${deal.slug}`)}
          />,
          document.body
        )}
    </>
  );
};

export default OrderDetailsModal;
